import { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { 
  ChevronDown, 
  TrendingUp, 
  Coins, 
  Package, 
  Gem, 
  Calculator, 
  BookOpen 
} from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

interface MobileNavItemsProps {
  isAuthenticated: boolean;
  userRole: string | null | undefined;
  onItemClick?: () => void;
  className?: string;
}

const oroRoutes = ["/oro-da-investimento", "/monete-oro-investimento", "/lingotti-oro-investimento"];
const diamantiRoutes = ["/diamanti", "/quotazione-diamanti", "/calcolatore-carati-diamante", "/le-4c-del-diamante"];

const getLinkClass = ({ isActive }: { isActive: boolean }) =>
  cn("block py-2 px-3 text-sm font-medium rounded-md transition-colors",
    isActive ? "bg-gold/10 text-gold border-l-2 border-gold" : "hover:bg-muted");

const getSubLinkClass = ({ isActive }: { isActive: boolean }) =>
  cn("flex items-center gap-2 py-2 px-3 text-sm rounded-md transition-colors",
    isActive ? "bg-gold/10 text-gold" : "hover:bg-muted");

export function MobileNavItems({ 
  isAuthenticated, 
  userRole, 
  onItemClick,
  className 
}: MobileNavItemsProps) {
  const location = useLocation();
  const isOroActive = oroRoutes.includes(location.pathname);
  const isDiamantiActive = diamantiRoutes.includes(location.pathname);

  const [oroOpen, setOroOpen] = useState(isOroActive);
  const [diamantiOpen, setDiamantiOpen] = useState(isDiamantiActive);

  // Auto-expand when navigating to a sub-route
  useEffect(() => {
    if (isOroActive) setOroOpen(true);
    if (isDiamantiActive) setDiamantiOpen(true);
  }, [isOroActive, isDiamantiActive]);

  return (
    <nav className={cn("flex flex-col space-y-1", className)}>
      <NavLink to="/" className={getLinkClass} onClick={onItemClick} end>
        Home
      </NavLink>
      
      <NavLink to="/compro-oro" className={getLinkClass} onClick={onItemClick}>
        Compro Oro
      </NavLink>

      {/* Oro da Investimento - Collapsible */}
      <Collapsible open={oroOpen} onOpenChange={setOroOpen}>
        <CollapsibleTrigger className={cn(
          "flex items-center justify-between w-full py-2 px-3 text-sm font-medium rounded-md transition-colors",
          isOroActive ? "bg-gold/10 text-gold" : "hover:bg-muted"
        )}>
          <span className="flex items-center gap-2">
            <TrendingUp className={cn("h-4 w-4", isOroActive ? "text-gold" : "text-amber-500")} />
            Oro da Investimento
          </span>
          <ChevronDown className={cn(
            "h-4 w-4 text-muted-foreground transition-transform duration-200",
            oroOpen && "rotate-180"
          )} />
        </CollapsibleTrigger>
        <CollapsibleContent className="pl-4 mt-1 space-y-1 border-l-2 border-amber-200 dark:border-amber-800 ml-3">
          <NavLink 
            to="/oro-da-investimento" 
            className={getSubLinkClass}
            onClick={onItemClick}
          >
            <TrendingUp className="h-4 w-4 text-amber-500" />
            <span>Guida Completa</span>
          </NavLink>
          <NavLink 
            to="/monete-oro-investimento" 
            className={getSubLinkClass}
            onClick={onItemClick}
          >
            <Coins className="h-4 w-4 text-amber-500" />
            <span>Monete d'Oro</span>
          </NavLink>
          <NavLink 
            to="/lingotti-oro-investimento" 
            className={getSubLinkClass}
            onClick={onItemClick}
          >
            <Package className="h-4 w-4 text-amber-500" />
            <span>Lingotti d'Oro</span>
          </NavLink>
        </CollapsibleContent>
      </Collapsible>

      {/* Diamanti - Collapsible */}
      <Collapsible open={diamantiOpen} onOpenChange={setDiamantiOpen}>
        <CollapsibleTrigger className={cn(
          "flex items-center justify-between w-full py-2 px-3 text-sm font-medium rounded-md transition-colors",
          isDiamantiActive ? "bg-gold/10 text-gold" : "hover:bg-muted"
        )}>
          <span className="flex items-center gap-2">
            <Gem className="h-4 w-4 text-gold" />
            Diamanti
          </span>
          <ChevronDown className={cn(
            "h-4 w-4 text-muted-foreground transition-transform duration-200",
            diamantiOpen && "rotate-180"
          )} />
        </CollapsibleTrigger>
        <CollapsibleContent className="pl-4 mt-1 space-y-1 border-l-2 border-gold/30 ml-3">
          <NavLink 
            to="/diamanti" 
            className={getSubLinkClass}
            onClick={onItemClick}
          >
            <Gem className="h-4 w-4 text-gold" />
            <span>Guida Completa</span>
          </NavLink>
          <NavLink 
            to="/quotazione-diamanti" 
            className={getSubLinkClass}
            onClick={onItemClick}
          >
            <Gem className="h-4 w-4 text-gold" />
            <span>Quotazione</span>
          </NavLink>
          <NavLink 
            to="/calcolatore-carati-diamante" 
            className={getSubLinkClass}
            onClick={onItemClick}
          >
            <Calculator className="h-4 w-4 text-gold" />
            <span>Calcolatore Carati</span>
          </NavLink>
          <NavLink 
            to="/le-4c-del-diamante" 
            className={getSubLinkClass}
            onClick={onItemClick}
          >
            <BookOpen className="h-4 w-4 text-gold" />
            <span>Le 4C</span>
          </NavLink>
        </CollapsibleContent>
      </Collapsible>

      <NavLink to="/grafici" className={getLinkClass} onClick={onItemClick}>
        Grafici
      </NavLink>
      
      <NavLink to="/avvisi" className={getLinkClass} onClick={onItemClick}>
        Avvisi
      </NavLink>
      
      <NavLink to="/assistente-ai" className={getLinkClass} onClick={onItemClick}>
        Assistente AI
      </NavLink>
      
      <NavLink to="/previsioni" className={getLinkClass} onClick={onItemClick}>
        Previsioni AI
      </NavLink>
      
      <NavLink to="/blog" className={getLinkClass} onClick={onItemClick}>
        Blog
      </NavLink>

      {isAuthenticated && (userRole === 'admin' || userRole === 'super_admin') && (
        <NavLink to="/admin" className={getLinkClass} onClick={onItemClick}>
          Admin
        </NavLink>
      )}
    </nav>
  );
}
